import { ChatBubble } from "@/components/ai/chat/ChatBubble";
import { TodoContainer } from "@/components/ai/chat/TodoContainer";
import { type AgentMessageFragment, type Message } from "@/hooks/use-stream-conversation";
import { type PlainAgentExecution } from "@/queries/agent-queries";
import { filterMessagesByType, MESSAGE_TYPES } from "@/utils/chat";
import { groupMessagesByTodo, calculateTodoGroupProgress, type GroupedMessage } from "@/utils/message-grouping";
import { useMemo, useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

export type GroupedMessagesHandle = {
    scrollToBottom: () => void;
};

export type GroupedMessagesProps = {
    messages: Message[];
    agentExecution: PlainAgentExecution;
    onClick?: (block: AgentMessageFragment) => void;
};

// Find the nearest scrollable parent element
function findScrollParent(element: HTMLElement | null): HTMLElement | null {
    if (!element) return null;

    let parent = element.parentElement;
    while (parent) {
        const { overflow, overflowY } = getComputedStyle(parent);
        if (overflow === "auto" || overflow === "scroll" || overflowY === "auto" || overflowY === "scroll") {
            return parent;
        }
        parent = parent.parentElement;
    }
    return document.documentElement;
}

export const GroupedMessages = forwardRef<GroupedMessagesHandle, GroupedMessagesProps>(
    ({ messages, agentExecution, onClick }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

        const groupedMessages = useMemo(() => groupMessagesByTodo(messages), [messages]);
        const agentMessages = useMemo(() => filterMessagesByType(messages, MESSAGE_TYPES.AGENT), [messages]);

        const prevLengthRef = useRef(groupedMessages.length);
        const isAtBottomRef = useRef(true);

        // Find scroll parent on mount
        useEffect(() => {
            if (containerRef.current) {
                const parent = findScrollParent(containerRef.current);
                setScrollParent(parent);
            }
        }, []);

        const virtualizer = useVirtualizer({
            count: groupedMessages.length,
            getScrollElement: () => scrollParent,
            estimateSize: () => 150,
            overscan: 5,
        });

        const items = virtualizer.getVirtualItems();

        // Expose scrollToBottom via ref
        useImperativeHandle(
            ref,
            () => ({
                scrollToBottom: () => {
                    if (groupedMessages.length > 0) {
                        virtualizer.scrollToIndex(groupedMessages.length - 1, { align: "end" });
                    }
                },
            }),
            [groupedMessages.length, virtualizer],
        );

        // Track if user is at bottom
        const handleScroll = useCallback(() => {
            if (!scrollParent) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollParent;
            isAtBottomRef.current = scrollTop + clientHeight >= scrollHeight - 100;
        }, [scrollParent]);

        useEffect(() => {
            if (!scrollParent) return;
            scrollParent.addEventListener("scroll", handleScroll);
            return () => scrollParent.removeEventListener("scroll", handleScroll);
        }, [scrollParent, handleScroll]);

        // Auto-scroll to bottom when new messages arrive (if already at bottom)
        useEffect(() => {
            if (groupedMessages.length > prevLengthRef.current && isAtBottomRef.current) {
                virtualizer.scrollToIndex(groupedMessages.length - 1, { align: "end" });
            }
            prevLengthRef.current = groupedMessages.length;
        }, [groupedMessages.length, virtualizer]);

        // Initial scroll to bottom
        useEffect(() => {
            if (groupedMessages.length > 0 && scrollParent) {
                requestAnimationFrame(() => {
                    virtualizer.scrollToIndex(groupedMessages.length - 1, { align: "end" });
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [scrollParent]);

    const renderGroup = (group: GroupedMessage, index: number) => {
        const isLastGroup = index === groupedMessages.length - 1;

        if (group.type === "user") {
            return (
                <div className="flex w-full justify-end" data-message-id={`user-${group.id}`}>
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={agentMessages}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "agent") {
            return (
                <div className="w-full">
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={agentMessages}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "todo-group") {
            const { started, total } = calculateTodoGroupProgress(group.todos);
            return (
                <div className="flex w-full flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <div className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-medium uppercase tracking-[0.5px] text-content-muted">
                            {group.isContinuation ? "TODOs (Continued)" : "TODOs"}
                        </div>
                        <span className="text-sm font-medium text-content-muted">
                            {started}/{total}
                        </span>
                    </div>
                    <div className="flex flex-col overflow-clip rounded-lg border border-border-base bg-surface-pure">
                        {group.todos.map((todo) => (
                            <TodoContainer
                                key={todo.todoId}
                                todo={todo}
                                isLastGroup={isLastGroup}
                                agentExecution={agentExecution}
                                onClick={onClick}
                                data-tracking-id="default-tracking-id"
                            />
                        ))}
                    </div>
                </div>
            );
        }
        return null;
    };

        return (
            <div
                ref={containerRef}
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                }}
            >
                {items.map((virtualItem) => {
                    const group = groupedMessages[virtualItem.index];
                    return (
                        <div
                            key={virtualItem.key}
                            data-index={virtualItem.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                            className="pb-4"
                        >
                            {renderGroup(group, virtualItem.index)}
                        </div>
                    );
                })}
            </div>
        );
    },
);

GroupedMessages.displayName = "GroupedMessages";
